import type { SalesDataPoint, TimePeriod } from "../types"
import { HierarchyLevel, SalesHierarchyNode } from "../types"

export interface BlankRowForecastDefaults {
  forecast_start_period: string
  cost_per_case: number
  same_store_sales_multiplier: number
  store_count_growth_multiplier: number
}

function new_stable_id(prefix: string): string {
  const suffix =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, "").slice(0, 12)
      : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  return `${prefix}-${suffix}`
}

function unique_row_label(base: string, id: string): string {
  return `${base} (${id.slice(-6)})`
}

/** Prefer an existing case cost from the hierarchy so new rows match the rest of the forecast. */
export function default_case_cost_from_hierarchy(nodes: SalesHierarchyNode[]): number {
  let cost = 10
  function walk(ns: SalesHierarchyNode[]): boolean {
    for (const n of ns) {
      const cc = (n as any).case_cost ?? (n as any).forecast_config?.cost_per_case
      if (cc != null && Number(cc) > 0) {
        cost = Number(cc)
        return true
      }
      if (n.children?.length && walk(n.children as SalesHierarchyNode[])) {
        return true
      }
    }
    return false
  }
  walk(nodes)
  return cost
}

/** Zero metrics across visible periods so rollups stay consistent until the user enters drivers. */
export function createBlankSalesDataForPeriods(
  periods: TimePeriod[],
  forecastStartPeriod: string,
  caseCost: number
): SalesDataPoint[] {
  return periods.map((period: TimePeriod) => ({
    period,
    metrics: {
      store_count: 0,
      volume_dollars: 0,
      cases_sold: 0,
      case_cost: caseCost,
    },
    is_forecast: period.period_key >= forecastStartPeriod,
    source_kind: "manual_entry" as const,
  }))
}

function createBlankNode(params: {
  id: string
  name: string
  level: HierarchyLevel
  parent_id: string | undefined
  sales_data: SalesDataPoint[]
  defaults: BlankRowForecastDefaults
}): SalesHierarchyNode {
  const cost = params.defaults.cost_per_case
  return {
    id: params.id,
    name: params.name,
    level: params.level,
    parent_id: params.parent_id,
    sales_data: params.sales_data,
    forecast_config: {
      initial_doors: 0,
      same_store_sales_multiplier: params.defaults.same_store_sales_multiplier,
      store_count_growth_multiplier: params.defaults.store_count_growth_multiplier,
      initial_cases_per_month: 0,
      cost_per_case: cost,
    },
    children: [],
  } as SalesHierarchyNode
}

function set_node_case_cost(node: SalesHierarchyNode, cost: number): void {
  ;(node as SalesHierarchyNode & { case_cost?: number }).case_cost = cost
}

/** Whether the tree already contains SKU-level detail (brand forecasts at SKU). */
export function inferForecastLeafLevel(nodes: SalesHierarchyNode[]): HierarchyLevel.PRODUCT_LINE | HierarchyLevel.SKU {
  let saw_sku = false
  function walk(ns: SalesHierarchyNode[]): void {
    for (const n of ns) {
      if (String(n.level) === HierarchyLevel.SKU || n.level === HierarchyLevel.SKU) {
        saw_sku = true
      }
      if (n.children?.length) {
        walk(n.children as SalesHierarchyNode[])
      }
    }
  }
  walk(nodes)
  return saw_sku ? HierarchyLevel.SKU : HierarchyLevel.PRODUCT_LINE
}

function append_child_under_parent(
  nodes: SalesHierarchyNode[],
  parent_id: string,
  child: SalesHierarchyNode
): SalesHierarchyNode[] {
  function map_node(n: SalesHierarchyNode): SalesHierarchyNode {
    if (n.id === parent_id) {
      const next_children = [...((n.children || []) as SalesHierarchyNode[]), child]
      return { ...(n as object), children: next_children } as SalesHierarchyNode
    }
    if (n.children?.length) {
      return {
        ...(n as object),
        children: (n.children as SalesHierarchyNode[]).map(map_node),
      } as SalesHierarchyNode
    }
    return n
  }
  return nodes.map(map_node)
}

/** A SKU picked from (or typed alongside) the product catalog for a structured quick-add. */
export interface CatalogSkuSelection {
  sku_name: string
  product_sku?: string
  product_id?: string
  product_upc?: string
  units_per_case?: number
  cost_per_case: number
}

export interface AddCatalogSkuParams {
  /** The product-line (or customer) row the SKU goes under. */
  target_row_id: string
  sku: CatalogSkuSelection
  initial_doors: number
  /** Same semantics as the create dialog's "Initial cases per month". */
  initial_cases_per_month: number
  start_month: string
  periods: TimePeriod[]
}

export interface AddCatalogSkuResult {
  nodes: SalesHierarchyNode[]
  new_node_id: string
  expand_ids: string[]
}

/**
 * Insert a catalog-identified SKU row under a product-line row, with generated forecast
 * data (doors × cases × cost from the start month) and the importer-convention
 * `sku:<upc>` stable key so actualization can resolve it — the structured alternative
 * to a blank "New SKU" row whose name has to be typed by hand.
 */
export function addCatalogSkuUnderRow(
  roots: SalesHierarchyNode[],
  params: AddCatalogSkuParams
): AddCatalogSkuResult | { error: string } {
  const { target_row_id, sku, initial_doors, initial_cases_per_month, start_month, periods } = params

  let target: SalesHierarchyNode | null = null
  const ancestor_chain: string[] = []
  function find(ns: SalesHierarchyNode[], chain: string[]): void {
    for (const n of ns) {
      if (n.id === target_row_id) {
        target = n
        ancestor_chain.push(...chain, n.id)
        return
      }
      if (n.children?.length) {
        find(n.children as SalesHierarchyNode[], [...chain, n.id])
        if (target) return
      }
    }
  }
  find(roots, [])
  if (!target) {
    return { error: "Could not find the row to add the SKU under" }
  }
  const target_level = String((target as SalesHierarchyNode).level)
  if (target_level !== HierarchyLevel.PRODUCT_LINE) {
    return { error: "SKUs are added under a product line row" }
  }

  const node_id = new_stable_id("sku")
  const identity = (sku.product_upc || sku.product_sku || "").trim().toLowerCase()
  const start_index = periods.findIndex((p: TimePeriod) => p.period_key === start_month)
  if (start_index < 0) {
    return { error: "Start month is not part of this forecast" }
  }
  const cost = sku.cost_per_case
  const sales_data: SalesDataPoint[] = periods.slice(start_index).map((period: TimePeriod) => ({
    period,
    metrics: {
      store_count: initial_doors,
      cases_sold: initial_cases_per_month,
      // Same planning model as the create dialog: dollars = doors × cases/month × cost.
      volume_dollars: initial_doors * initial_cases_per_month * cost,
      case_cost: cost,
    },
    is_forecast: true,
    source_kind: "forecast" as const,
  }))

  const sku_node = {
    id: node_id,
    name: sku.sku_name,
    level: HierarchyLevel.SKU,
    parent_id: target_row_id,
    ...(identity ? { stable_key: `sku:${identity}` } : {}),
    product_sku: sku.product_sku,
    sku_name: sku.sku_name,
    case_cost: cost,
    ...(sku.product_id || sku.units_per_case
      ? {
          metadata: {
            ...(sku.product_id ? { catalog_product_id: sku.product_id } : {}),
            ...(sku.units_per_case ? { units_per_case: sku.units_per_case } : {}),
          },
        }
      : {}),
    sales_data,
    forecast_config: {
      initial_doors,
      initial_cases_per_month,
      cost_per_case: cost,
      same_store_sales_multiplier: 0,
      store_count_growth_multiplier: 0,
    },
    children: [],
  } as unknown as SalesHierarchyNode

  return {
    nodes: append_child_under_parent(roots, target_row_id, sku_node),
    new_node_id: node_id,
    expand_ids: [...ancestor_chain, node_id],
  }
}

export interface AddHierarchyRowParams {
  context_row_id: string
  context_row_level: HierarchyLevel | string
  leaf_level: HierarchyLevel.PRODUCT_LINE | HierarchyLevel.SKU
  periods: TimePeriod[]
  defaults: BlankRowForecastDefaults
}

export interface AddHierarchyRowResult {
  nodes: SalesHierarchyNode[]
  /** Expand these row ids (ancestors + new nodes) so the user sees the insertion. */
  expand_ids: string[]
}

/**
 * Insert blank hierarchy rows based on the clicked row and the forecast granularity already used in the version.
 */
export function addHierarchyRowsUnderContext(
  roots: SalesHierarchyNode[],
  params: AddHierarchyRowParams
): AddHierarchyRowResult {
  const {
    context_row_id,
    context_row_level,
    leaf_level,
    periods,
    defaults,
  } = params
  const level_str = String(context_row_level)
  const forecast_start = defaults.forecast_start_period
  const cost = defaults.cost_per_case
  const blank_periods = (): SalesDataPoint[] =>
    createBlankSalesDataForPeriods(periods, forecast_start, cost)

  let next = structuredClone(roots) as SalesHierarchyNode[]
  const expand_ids = new Set<string>()

  function track_expand(id_chain: string[]): void {
    id_chain.forEach((id: string) => expand_ids.add(id))
  }

  const cot_like =
    level_str === HierarchyLevel.CLASS_OF_TRADE ||
    String(level_str) === "class_of_trade"
  const buyer_like =
    level_str === HierarchyLevel.CUSTOMER ||
    level_str === HierarchyLevel.RETAILER ||
    String(level_str) === "customer" ||
    level_str === HierarchyLevel.DISTRIBUTOR ||
    String(level_str) === "distributor"

  // Blank SKU rows are never created: SKUs enter through the catalog picker
  // (`addCatalogSkuUnderRow`) so every SKU row carries a real identity. Blank adds
  // stop at the product line, which the catalog dialog then fills.

  // Class of trade → new buyer account + nested PL
  if (cot_like && context_row_id) {
    const cust_id = new_stable_id("customer")
    const cust = createBlankNode({
      id: cust_id,
      name: unique_row_label("New customer", cust_id),
      level: HierarchyLevel.CUSTOMER,
      parent_id: context_row_id,
      sales_data: [],
      defaults,
    })
    const pl_id = new_stable_id("pl")
    const pl = createBlankNode({
      id: pl_id,
      name: unique_row_label("New product line", pl_id),
      level: HierarchyLevel.PRODUCT_LINE,
      parent_id: cust_id,
      sales_data: blank_periods(),
      defaults,
    })
    set_node_case_cost(pl, cost)
    cust.children = [pl]
    track_expand([context_row_id, cust_id, pl_id])
    next = append_child_under_parent(next, context_row_id, cust)
    return { nodes: next, expand_ids: [...expand_ids] }
  }

  // Buyer → new PL
  if (buyer_like && context_row_id) {
    const pl_id = new_stable_id("pl")
    const pl = createBlankNode({
      id: pl_id,
      name: unique_row_label("New product line", pl_id),
      level: HierarchyLevel.PRODUCT_LINE,
      parent_id: context_row_id,
      sales_data: blank_periods(),
      defaults,
    })
    set_node_case_cost(pl, cost)
    next = append_child_under_parent(next, context_row_id, pl)
    track_expand([context_row_id, pl_id])
    return { nodes: next, expand_ids: [...expand_ids] }
  }

  // Product line with SKU-level detail → SKUs come from the catalog picker (the caller
  // routes to the Add SKU dialog); nothing to insert blank here.
  if (
    (level_str === HierarchyLevel.PRODUCT_LINE || String(level_str) === "product_line") &&
    leaf_level === HierarchyLevel.SKU &&
    context_row_id
  ) {
    return { nodes: roots, expand_ids: [] }
  }

  // Product-line-only forecast → add sibling PL under same parent as context PL
  if (
    (level_str === HierarchyLevel.PRODUCT_LINE || String(level_str) === "product_line") &&
    leaf_level === HierarchyLevel.PRODUCT_LINE &&
    context_row_id
  ) {
    const parent_of_pl = find_parent_id(next, context_row_id)
    if (!parent_of_pl) {
      return { nodes: roots, expand_ids: [] }
    }
    const pl_id = new_stable_id("pl")
    const pl = createBlankNode({
      id: pl_id,
      name: unique_row_label("New product line", pl_id),
      level: HierarchyLevel.PRODUCT_LINE,
      parent_id: parent_of_pl,
      sales_data: blank_periods(),
      defaults,
    })
    set_node_case_cost(pl, cost)
    next = append_child_under_parent(next, parent_of_pl, pl)
    track_expand([parent_of_pl, pl_id])
    return { nodes: next, expand_ids: [...expand_ids] }
  }

  // SKU context → sibling SKUs also come from the catalog picker.
  return { nodes: roots, expand_ids: [] }
}

/** Parent product-line node id of a SKU row, for routing sibling adds to the catalog dialog. */
export function find_product_line_parent_id(roots: SalesHierarchyNode[], sku_row_id: string): string | null {
  return find_parent_id(structuredClone(roots) as SalesHierarchyNode[], sku_row_id)
}

function find_parent_id(nodes: SalesHierarchyNode[], child_id: string): string | null {
  function walk(ns: SalesHierarchyNode[], parent_id: string | null): string | null {
    for (const n of ns) {
      if (n.id === child_id) {
        return parent_id
      }
      if (n.children?.length) {
        const found = walk(n.children as SalesHierarchyNode[], n.id)
        if (found !== null) {
          return found
        }
      }
    }
    return null
  }
  return walk(nodes, null)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("inferForecastLeafLevel", () => {
    it("returns SKU when any SKU exists", () => {
      const nodes = [
        {
          id: "r",
          name: "R",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [
            {
              id: "c",
              name: "Customer",
              level: HierarchyLevel.CUSTOMER,
              parent_id: "r",
              sales_data: [],
              children: [
                {
                  id: "pl",
                  name: "Product Line",
                  level: HierarchyLevel.PRODUCT_LINE,
                  parent_id: "c",
                  sales_data: [],
                  children: [{ id: "s", name: "SKU", level: HierarchyLevel.SKU, parent_id: "pl", sales_data: [] }],
                },
              ],
            },
          ],
        },
      ] as SalesHierarchyNode[]
      expect(inferForecastLeafLevel(nodes)).toBe(HierarchyLevel.SKU)
    })

    it("returns PRODUCT_LINE when no SKU exists", () => {
      const nodes = [
        {
          id: "r",
          name: "R",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [
            {
              id: "c",
              name: "Customer",
              level: HierarchyLevel.CUSTOMER,
              parent_id: "r",
              sales_data: [],
              children: [{ id: "pl", name: "Product Line", level: HierarchyLevel.PRODUCT_LINE, parent_id: "c", sales_data: [] }],
            },
          ],
        },
      ] as SalesHierarchyNode[]
      expect(inferForecastLeafLevel(nodes)).toBe(HierarchyLevel.PRODUCT_LINE)
    })
  })

  describe("addHierarchyRowsUnderContext", () => {
    const periods: TimePeriod[] = [
      { year: 2026, month: 1, period_key: "2026-01" },
    ]
    const defaults: BlankRowForecastDefaults = {
      forecast_start_period: "2026-01",
      cost_per_case: 10,
      same_store_sales_multiplier: 0.03,
      store_count_growth_multiplier: 0.02,
    }

    it("adds customer + PL under class of trade but never a blank SKU (catalog picker owns SKUs)", () => {
      const roots = [
        {
          id: "cot",
          name: "Natural",
          level: HierarchyLevel.CLASS_OF_TRADE,
          sales_data: [],
          children: [],
        },
      ] as SalesHierarchyNode[]
      const { nodes, expand_ids } = addHierarchyRowsUnderContext(roots, {
        context_row_id: "cot",
        context_row_level: HierarchyLevel.CLASS_OF_TRADE,
        leaf_level: HierarchyLevel.SKU,
        periods,
        defaults,
      })
      expect(nodes[0].children).toHaveLength(1)
      const cust = (nodes[0] as any).children[0]
      expect(cust.children).toHaveLength(1)
      const pl = cust.children[0]
      expect(pl.level).toBe(HierarchyLevel.PRODUCT_LINE)
      // A free-text "New SKU (abc123)" row was the unstructured escape hatch — gone.
      expect(pl.children ?? []).toHaveLength(0)
      expect(expand_ids.length).toBeGreaterThan(0)
    })

    it("returns empty for PL/SKU contexts in SKU-grain trees (caller opens the catalog dialog)", () => {
      const roots = [
        {
          id: "pl",
          name: "Sparkling",
          level: HierarchyLevel.PRODUCT_LINE,
          sales_data: [],
          children: [{ id: "sku-1", name: "Yuzu", level: HierarchyLevel.SKU, sales_data: [], children: [] }],
        },
      ] as unknown as SalesHierarchyNode[]
      const from_pl = addHierarchyRowsUnderContext(roots, {
        context_row_id: "pl",
        context_row_level: HierarchyLevel.PRODUCT_LINE,
        leaf_level: HierarchyLevel.SKU,
        periods,
        defaults,
      })
      expect(from_pl.expand_ids).toHaveLength(0)
      const from_sku = addHierarchyRowsUnderContext(roots, {
        context_row_id: "sku-1",
        context_row_level: HierarchyLevel.SKU,
        leaf_level: HierarchyLevel.SKU,
        periods,
        defaults,
      })
      expect(from_sku.expand_ids).toHaveLength(0)
    })
  })

  describe("addCatalogSkuUnderRow", () => {
    const periods: TimePeriod[] = [
      { year: 2027, month: 1, period_key: "2027-01" },
      { year: 2027, month: 2, period_key: "2027-02" },
      { year: 2027, month: 3, period_key: "2027-03" },
    ]
    const tree = () =>
      [
        {
          id: "cust-1",
          name: "Kroger",
          level: HierarchyLevel.CUSTOMER,
          sales_data: [],
          children: [
            { id: "pl-1", name: "Sparkling", level: HierarchyLevel.PRODUCT_LINE, sales_data: [], children: [] },
          ],
        },
      ] as unknown as SalesHierarchyNode[]

    it("inserts a catalog-keyed SKU with generated forecast data from the start month", () => {
      const result = addCatalogSkuUnderRow(tree(), {
        target_row_id: "pl-1",
        sku: {
          sku_name: "Yuzu 12oz",
          product_sku: "SZ-001",
          product_id: "prod-1",
          product_upc: "840269200397",
          units_per_case: 12,
          cost_per_case: 13.56,
        },
        initial_doors: 50,
        initial_cases_per_month: 20,
        start_month: "2027-02",
        periods,
      })
      expect("error" in result).toBe(false)
      const { nodes, new_node_id, expand_ids } = result as AddCatalogSkuResult
      const sku = (nodes[0].children![0] as any).children[0]
      expect(sku.id).toBe(new_node_id)
      expect(sku.stable_key).toBe("sku:840269200397")
      expect(sku.metadata.catalog_product_id).toBe("prod-1")
      // Starts at the chosen month: Jan has no row, Feb+Mar generated.
      expect(sku.sales_data).toHaveLength(2)
      expect(sku.sales_data[0].period.period_key).toBe("2027-02")
      expect(sku.sales_data[0].metrics.volume_dollars).toBeCloseTo(50 * 20 * 13.56)
      expect(sku.sales_data[0].metrics.store_count).toBe(50)
      expect(expand_ids).toContain("cust-1")
      expect(expand_ids).toContain(new_node_id)
    })

    it("refuses non-product-line targets and unknown start months", () => {
      const on_customer = addCatalogSkuUnderRow(tree(), {
        target_row_id: "cust-1",
        sku: { sku_name: "X", cost_per_case: 10 },
        initial_doors: 1,
        initial_cases_per_month: 1,
        start_month: "2027-01",
        periods,
      })
      expect("error" in on_customer).toBe(true)

      const bad_month = addCatalogSkuUnderRow(tree(), {
        target_row_id: "pl-1",
        sku: { sku_name: "X", cost_per_case: 10 },
        initial_doors: 1,
        initial_cases_per_month: 1,
        start_month: "2099-01",
        periods,
      })
      expect("error" in bad_month).toBe(true)
    })

    it("falls back to the SKU code for identity and omits the key without one", () => {
      const with_code = addCatalogSkuUnderRow(tree(), {
        target_row_id: "pl-1",
        sku: { sku_name: "X", product_sku: "AB-9", cost_per_case: 10 },
        initial_doors: 1,
        initial_cases_per_month: 1,
        start_month: "2027-01",
        periods,
      }) as AddCatalogSkuResult
      expect(((with_code.nodes[0].children![0] as any).children[0]).stable_key).toBe("sku:ab-9")

      const nameless = addCatalogSkuUnderRow(tree(), {
        target_row_id: "pl-1",
        sku: { sku_name: "Free Text Flavor", cost_per_case: 10 },
        initial_doors: 1,
        initial_cases_per_month: 1,
        start_month: "2027-01",
        periods,
      }) as AddCatalogSkuResult
      expect(((nameless.nodes[0].children![0] as any).children[0]).stable_key).toBeUndefined()
    })
  })
}
